/**
 * The product catalogue, section 4.3.
 *
 * Item names and reference unit prices in USD come from the client's own price
 * list and are ported verbatim from the prototype's CATALOG. The sales report
 * groups by item name, so a name must match the price list exactly. Do not
 * tidy the spacing, the casing or the punctuation of a name.
 *
 * Prices are shown only to roles holding `pay` or `admin`, and the server omits
 * them for everyone else, section 7.10 item 2.
 */

/**
 * How a category is worded. Section 7 of the request of 16 September 2026: the
 * Items screen offered five categories and no way to make a sixth, so a lab
 * that starts selling something it has no heading for cannot record it under
 * one.
 *
 * A category is a WORD ON AN ITEM, not a row of its own, which is why making
 * one is making an item in it and why a category with nothing in it does not
 * exist. That is the honest model for a heading the price list groups by, and
 * it means a category needs no screen of its own to be created, renamed or
 * retired: the items do all three.
 */
export const CATEGORY_MIN = 2;
export const CATEGORY_MAX = 32;

export const CATEGORY_REFUSAL = {
  required: 'Choose a category, or name a new one',
  tooShort: `A category name is at least ${CATEGORY_MIN} characters`,
  tooLong: `Keep a category name to ${CATEGORY_MAX} characters`,
} as const;

/**
 * The categories the lab started with. They are the ones offered first and the
 * ones the seed writes; they are NOT the only ones allowed.
 */
export const CATALOG_CATEGORIES = ['Zircon', 'E.max', 'Metal', 'Removable', 'Additional'] as const;
export type CatalogCategory = (typeof CATALOG_CATEGORIES)[number];

/** What is wrong with a category name, or null when it will do. The server's wording. */
export function refuseCategory(name: string): string | null {
  const trimmed = name.trim();
  if (trimmed === '') return CATEGORY_REFUSAL.required;
  if (trimmed.length < CATEGORY_MIN) return CATEGORY_REFUSAL.tooShort;
  if (trimmed.length > CATEGORY_MAX) return CATEGORY_REFUSAL.tooLong;
  return null;
}

/**
 * Every heading to show: the five the lab started with, in their order, then
 * any the lab has added, in the order they are met. Request of 5 October 2026:
 * Accounts name new categories on the Items screen, and intake offers them.
 */
export function categoriesOf(used: Iterable<string>): string[] {
  const added: string[] = [];
  for (const category of used) {
    if (!(CATALOG_CATEGORIES as readonly string[]).includes(category) && !added.includes(category))
      added.push(category);
  }
  return [...CATALOG_CATEGORIES, ...added];
}

/**
 * How many kits a job order line produces.
 *
 * `unit` makes one kit per selected tooth, `span` makes a single kit covering
 * every selected tooth. Section 7.5 requires the server to decide this from the
 * catalogue rather than from the client, so it is a property of the item here
 * rather than a rule applied at intake.
 */
export type ProductKind = 'unit' | 'span';

export interface CatalogItem {
  /** A slug of the name as first listed. Stable: a rename keeps it. */
  id: string;
  name: string;
  category: CatalogCategory;
  unitPrice: number;
  kind: ProductKind;
}

/**
 * The id a catalogue item is listed under: a slug of its name as the price list
 * first had it. Deterministic, so the seed, the mock and the tests agree on it,
 * and stable, because a rename changes the name and never the id.
 */
export function catalogSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const PRICE_LIST: readonly Omit<CatalogItem, 'id'>[] = [
  { name: 'Zircon Crown', category: 'Zircon', unitPrice: 100, kind: 'unit' },
  { name: 'Zircon Crown over Implant', category: 'Zircon', unitPrice: 110, kind: 'unit' },
  { name: 'Zircon Maryland Bridge', category: 'Zircon', unitPrice: 250, kind: 'span' },
  { name: 'Zircon Rest', category: 'Zircon', unitPrice: 50, kind: 'unit' },
  { name: 'Zircon Post', category: 'Zircon', unitPrice: 70, kind: 'unit' },
  { name: 'E.max Veneer', category: 'E.max', unitPrice: 100, kind: 'unit' },
  { name: 'E.max Crown', category: 'E.max', unitPrice: 100, kind: 'unit' },
  { name: 'E.max Inlay / Onlay', category: 'E.max', unitPrice: 75, kind: 'unit' },
  { name: 'E.max Maryland Bridge', category: 'E.max', unitPrice: 200, kind: 'span' },
  { name: 'Crown PFM', category: 'Metal', unitPrice: 100, kind: 'unit' },
  { name: 'Hybrid Metal Frame with Gingiva', category: 'Metal', unitPrice: 800, kind: 'unit' },
  { name: 'Full Arch Titanium over Implant', category: 'Metal', unitPrice: 1500, kind: 'span' },
  { name: 'Full Arch Reinforced Titanium Bar', category: 'Metal', unitPrice: 1500, kind: 'span' },
  { name: 'Titanium Reinforced Bar 1-5 Teeth', category: 'Metal', unitPrice: 1000, kind: 'unit' },
  { name: 'Bleaching Tray', category: 'Removable', unitPrice: 50, kind: 'unit' },
  { name: 'Gingivectomy Guide', category: 'Removable', unitPrice: 150, kind: 'unit' },
  { name: 'Night Guard', category: 'Removable', unitPrice: 50, kind: 'unit' },
  { name: 'Ortho E6', category: 'Removable', unitPrice: 50, kind: 'unit' },
  { name: 'Snap On', category: 'Removable', unitPrice: 125, kind: 'unit' },
  { name: 'Denture 1-4 Acrylic', category: 'Removable', unitPrice: 150, kind: 'unit' },
  { name: 'Denture 1-4 Flexible', category: 'Removable', unitPrice: 200, kind: 'unit' },
  { name: 'Denture 5-12 Acrylic', category: 'Removable', unitPrice: 200, kind: 'unit' },
  { name: 'Denture 5-12 Flexible', category: 'Removable', unitPrice: 250, kind: 'unit' },
  { name: 'Special Tray', category: 'Additional', unitPrice: 55, kind: 'unit' },
  { name: 'Temporary Crown', category: 'Additional', unitPrice: 20, kind: 'unit' },
  { name: 'Abutment Preparation', category: 'Additional', unitPrice: 10, kind: 'unit' },
  { name: 'Mockup', category: 'Additional', unitPrice: 100, kind: 'unit' },
  { name: 'Study Models', category: 'Additional', unitPrice: 50, kind: 'unit' },
];

export const CATALOG: readonly CatalogItem[] = PRICE_LIST.map((item) => ({
  id: catalogSlug(item.name),
  ...item,
}));

const BY_NAME = new Map(CATALOG.map((item) => [item.name, item]));

export function catalogItem(name: string): CatalogItem | undefined {
  return BY_NAME.get(name);
}

/** An unknown item is Additional, matching the prototype's catOf. */
export function categoryOf(name: string): CatalogCategory {
  return catalogItem(name)?.category ?? 'Additional';
}

/** An unknown item is worth nothing, matching the prototype's priceOf. */
export function unitPriceOf(name: string): number {
  return catalogItem(name)?.unitPrice ?? 0;
}

export function isSpanProduct(name: string): boolean {
  return catalogItem(name)?.kind === 'span';
}

export function catalogByCategory(): Record<CatalogCategory, CatalogItem[]> {
  const grouped = {} as Record<CatalogCategory, CatalogItem[]>;
  for (const category of CATALOG_CATEGORIES) grouped[category] = [];
  for (const item of CATALOG) grouped[item.category].push(item);
  return grouped;
}

/** Section 4.3. Offered in the intake form. */
export const SHADES = ['A1', 'A2', 'A3', 'B1', 'B2', 'C1', 'BL2'] as const;
export type Shade = (typeof SHADES)[number];

/**
 * Section 4.3. The tracking code is always typed by hand from the waybill and
 * is never generated, which is item 5 of section 17.
 *
 * `Other` is the fifth choice, from section 8.2 of the request of
 * 16 September 2026: a case going out with anybody but these four could not be
 * recorded at all, and a lab that sends one parcel a quarter by a local firm
 * should not have to wait for a deploy to write its name down.
 *
 * The rest of the field is NOT free text, and should not become it. The four
 * are what the lab uses daily, and typing DHL four different ways is how a
 * carrier report becomes useless.
 */
export const CARRIERS = ['DHL Express', 'UPS', 'Aramex', 'Own courier', 'Other'] as const;
export type Carrier = (typeof CARRIERS)[number];

/** The choice that asks for a name. Compared against, so it is named once. */
export const CARRIER_OTHER = 'Other';

/** Short enough to be a name, long enough for "Emirates Post Group Express". */
export const CARRIER_OTHER_MIN = 2;
export const CARRIER_OTHER_MAX = 60;

/**
 * The courier as every screen and every report should read it: the name that
 * was typed when the choice was `Other`, and the choice itself otherwise.
 *
 * The server resolves this once, on the way in, and stores the answer. Nothing
 * downstream branches, because a carrier is a carrier whichever list it came
 * off, and a hundred display sites each remembering to check for `Other` is a
 * hundred chances to forget.
 */
export function resolveCarrier(carrier: string | null, carrierOther?: string | null): string | null {
  if (carrier === null) return null;
  if (carrier !== CARRIER_OTHER) return carrier;
  const named = (carrierOther ?? '').trim();
  return named === '' ? null : named;
}

/** Zirconia blocks offered in the specification panel, from the prototype. */
export const ZIRCONIA_BLOCKS = ['Katana STML', 'Katana UTML', 'DD Bio ML', 'Cercon ht'] as const;

/**
 * Press ingots, for the seed and for the datalist beside the free text field.
 *
 * A suggestion list and not a closed set: the client asked for a text input,
 * because a lab that has just opened a new box of something should not have to
 * wait for a deploy to write its name down.
 */
export const PRESS_INGOTS = [
  'IPS e.max Press LT',
  'IPS e.max Press HT',
  'IPS e.max Press MO',
  'Celtra Press',
] as const;
